import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from './api';

/**
 * What a table remembers about how somebody had it set up.
 *
 * Deliberately one object rather than four hooks' worth of separate settings: it is saved,
 * restored and compared as a unit, and a view that restored the filters but not the sort
 * would be worse than none.
 */
export interface TableState {
  search: string;
  sort: { key: string; direction: 'asc' | 'desc' } | null;
  /** Column key → what was typed or chosen. Absent means that column is not filtered. */
  filters: Record<string, string>;
  /** Column keys turned off. Keys, not indexes, so adding a column never shifts them. */
  hidden: string[];
  /** 0 means all of them. */
  pageSize: number;
  /**
   * The page's OWN filters, when it has some the table cannot express — "assigned to me",
   * "overdue", a date range.
   *
   * Opaque here and opaque on the server. It is carried so that a saved view means what
   * somebody expects it to mean: a view called "my open bugs" that restored the sort but not
   * the "mine" chip would be worse than no view at all.
   */
  page?: unknown;
}

export interface SavedView {
  id: string;
  tableKey: string;
  name: string;
  isDefault: boolean;
  state: Partial<TableState>;
  createdAt: string;
}

export const EMPTY_TABLE_STATE: TableState = {
  search: '',
  sort: null,
  filters: {},
  hidden: [],
  pageSize: 25,
};

/** A stored view filled out, so a view saved before a setting existed still restores cleanly. */
export function stateFrom(saved: Partial<TableState> | undefined, fallback: TableState): TableState {
  return {
    search: saved?.search ?? '',
    sort: saved?.sort ?? null,
    filters: saved?.filters ?? {},
    hidden: saved?.hidden ?? [],
    pageSize: saved?.pageSize ?? fallback.pageSize,
    page: saved?.page,
  };
}

/**
 * One person's saved arrangements of one list.
 *
 * Every call is scoped to them by the API — there is no "whose" to pass — so nothing here
 * decides visibility. Failures are swallowed into `error` rather than thrown: a list whose
 * saved views could not load is still a working list, and taking the table down with it
 * would be the wrong trade.
 */
export function useTableViews(tableKey: string) {
  const [views, setViews] = useState<SavedView[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setViews(await api.get<SavedView[]>(`/table-views?table=${encodeURIComponent(tableKey)}`));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load your saved views');
    } finally {
      setLoaded(true);
    }
  }, [tableKey]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = useCallback(
    async (name: string, state: TableState) => {
      const view = await api.post<SavedView>(`/table-views/${encodeURIComponent(tableKey)}`, { name, state });
      await load();
      return view;
    },
    [tableKey, load],
  );

  const update = useCallback(
    async (id: string, patch: { name?: string; state?: TableState; isDefault?: boolean }) => {
      const view = await api.patch<SavedView>(`/table-views/${id}`, patch);
      await load();
      return view;
    },
    [load],
  );

  const remove = useCallback(
    async (id: string) => {
      await api.del(`/table-views/${id}`);
      await load();
    },
    [load],
  );

  return { views, loaded, error, save, update, remove, reload: load };
}
